// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import Latex from './Latex.js';
import { LatexTextAtom } from './LatexTextAtom.js';
import ExternalLink from './ExternalLink.js';
import { AlgebraicWorkingStepIndicator } from './AlgebraicWorking.js';
import { SolidifyGlossaryTerm } from './solidify-lesson/SolidifyGlossaryTerm.js';
import { SolidifyEmphasis } from './solidify-lesson/SolidifyEmphasis.js';
import { SolidifyPartIndicator } from './solidify-lesson/SolidifyPartIndicator.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import { RSLGlossaryTerm } from './rsl-docs/common-components/RSLGlossaryTerm.js';
import { RSLEmphasis } from './rsl-docs/common-components/RSLEmphasis.js';
import { RSLPriorKnowledgeLink } from './rsl-docs/common-components/RSLPriorKnowledgeLink.js';
import { INVEmphasis } from './inv-docs/investigation/INVEmphasis.js';
import { INVInternalLessonLink } from './inv-docs/investigation/INVInternalLessonLink.js';

// NOTE: We will try to make Text block level always so content authors
// don't need to reason about block vs inline level text. This decision
// may break down eventually.
function Text(props: {|
  // We sometimes *internally* want to configure Text to be inline. This
  // should never be exposed to content authors (hence not being in the spec)
  _display?: string,
  children: ChildrenArray<
    | string
    | Element<typeof Bold>
    | Element<typeof Italics>
    | Element<typeof Latex>
    | Element<typeof LatexTextAtom>
    | Element<typeof ExternalLink>
    | Element<typeof AlgebraicWorkingStepIndicator>
    | Element<typeof SolidifyGlossaryTerm>
    | Element<typeof SolidifyEmphasis>
    | Element<typeof SolidifyPartIndicator>
    | Element<typeof RSLGlossaryTerm>
    | Element<typeof RSLEmphasis>
    | Element<typeof RSLPriorKnowledgeLink>
    | Element<typeof INVEmphasis>
    | Element<typeof INVInternalLessonLink>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    Text,
  );

  // PONDER
  // - Hard/impossible to express this type of constraint in the current spec system
  // - ad-hoc assertions also cannot block render due to delayed scheduling in useEffect...
  const childrenArray = React.Children.toArray(props.children);
  React.useEffect(() => {
    if (!props._performValidation) return;
    if (
      childrenArray.length === 1 &&
      typeof childrenArray[0] === 'string' &&
      childrenArray[0].trim() === ''
    ) {
      addErrors([
        {
          message: `<Text> must contain actual text. Empty components should never be used to try to influence the spacing/layout.`,
          ...locations.children,
        },
      ]);
    }
  });

  if (failedValidation) return null;

  return (
    <p style={{ display: props._display || 'block', margin: 0 }}>
      {props.children}
    </p>
  );
}
Text.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Bold),
      t.elementType(Italics),
      t.elementType(Latex), // TODO iff not displayMode
      t.elementType(LatexTextAtom),
      // NOTE here the current spec system starts breaking down. We only want ExternalLink
      // to be allowed when we are writing in MathVP documents (for now anyways)
      t.elementType(ExternalLink),
      t.elementType(AlgebraicWorkingStepIndicator),
      t.elementType(SolidifyGlossaryTerm),
      t.elementType(SolidifyEmphasis),
      t.elementType(SolidifyPartIndicator),
      t.elementType(RSLGlossaryTerm),
      t.elementType(RSLEmphasis),
      t.elementType(RSLPriorKnowledgeLink),
      t.elementType(INVEmphasis),
      t.elementType(INVInternalLessonLink),
    ),
  ),
};

function Bold(props: {| children: string | Iterable<string> |}) {
  const { failedValidation } = usePropsValidation(props, Bold);
  if (failedValidation) return null;
  const { children } = props;
  return <b style={{ fontWeight: 'bold' }}>{children}</b>;
}
Bold.spec = {
  children: t.childrenArray(t.string), // Supports <Bold>blah{' '}</Bold>
};

function Italics(props: {| children: string | Iterable<string> |}) {
  const { failedValidation } = usePropsValidation(props, Italics);
  if (failedValidation) return null;
  const { children } = props;
  return <i style={{ fontStyle: 'italic' }}>{children}</i>;
}
Italics.spec = {
  children: t.childrenArray(t.string), // Supports <Italics>blah{' '}</Italics>
};

export { Text, Bold, Italics };
